import React from 'react';
import { useTranslation } from 'react-i18next';
import { AgGridReact } from 'ag-grid-react';
import { ITrack } from '@/data/models/track';
import TriStateCheckbox from '@/components/common/TriStateCheckbox/TriStateCheckbox';
import ReorderTracks from './ReorderTracks/ReorderTracks';
import VolumeMenu from './VolumeMenu/VolumeMenu';
import { ProductViewModes } from '@/utils/productStateUtils';
import { ICurrentTracksChangesGroup } from '@/types/product-current-changes-types';

export interface TrackGridProps {
  showReorderTracks: boolean;
  setShowReorderTracks: (show: boolean) => void;
  tracks: ITrack[];
  volumeNumbers: number[];
  updateProductTracks: (tracks: ITrack[]) => Promise<void>;
  formatHeader: (volumeNumber: number) => string;
  readonly: boolean;
  isSelectedAllChecked: boolean;
  isSelectedAllIntermediate: boolean;
  handleSelectAllTracks: () => void;
  editSelectedTracks: () => void;
  handleCopyProductMetadataClick: () => void;
  handleAddToVolumeClick: () => void;
  usesSides: boolean;
  deleteSelectedTracks: () => void;
  AddVolume: (action: string, args: any) => void;
  addNewTrack: (volumeNumber: number) => void;
  addMultipleTracks: (volumeNumber: number) => void;
  gridsRef: React.MutableRefObject<Map<string, any>> | any;
  rowSelection: any;
  onSelectionChanged: () => void;
  getLocaleText: (params: any) => string;
  finalColumnDefs: any[];
  addRemoveTrack: (track: ITrack, option: string) => Promise<void>;
  toggleTrackPopUp: (track: ITrack) => void;
  getRowId: (params: any) => string;
  productViewMode?: ProductViewModes;
  trackChangeRequestChanges?: ICurrentTracksChangesGroup;
}

export default function TrackGrid({
  showReorderTracks,
  setShowReorderTracks,
  tracks,
  volumeNumbers,
  updateProductTracks,
  formatHeader,
  readonly,
  isSelectedAllChecked,
  isSelectedAllIntermediate,
  handleSelectAllTracks,
  editSelectedTracks,
  handleCopyProductMetadataClick,
  handleAddToVolumeClick,
  usesSides,
  deleteSelectedTracks,
  AddVolume,
  addNewTrack,
  addMultipleTracks,
  gridsRef,
  rowSelection,
  onSelectionChanged,
  getLocaleText,
  finalColumnDefs,
  addRemoveTrack,
  toggleTrackPopUp,
  getRowId,
  productViewMode,
  trackChangeRequestChanges,
}: TrackGridProps) {
  const { t } = useTranslation();
  const isChangeRequestMode =
    productViewMode === ProductViewModes.ChangeRequest;

  return (
    <>
      {showReorderTracks ? (
        <ReorderTracks
          tracks={tracks}
          volumeNumbers={volumeNumbers}
          updateProductTracks={updateProductTracks}
          closeComponent={() => setShowReorderTracks(false)}
          formatVolumeHeader={formatHeader}
        ></ReorderTracks>
      ) : (
        <>
          {!readonly && tracks.length > 0 ? (
            <div className={'track-actions-container'}>
              <div className="track-select-all-tracks">
                <TriStateCheckbox
                  checked={isSelectedAllChecked}
                  indeterminate={isSelectedAllIntermediate}
                  onClick={handleSelectAllTracks}
                ></TriStateCheckbox>
                <button
                  type="button"
                  className={`btn btn-primary alt ${isSelectedAllChecked ? 'padding-left-x1 padding-right-0' : 'padding-left-x2'}`}
                  onClick={handleSelectAllTracks}
                >
                  {!isSelectedAllChecked
                    ? t('common.button.selectAllTracks')
                    : ''}
                </button>
              </div>
              {isSelectedAllChecked || isSelectedAllIntermediate ? (
                <>
                  {/* Edit Selected Tracks Button */}
                  <div className="track-reorder-buttons">
                    <button
                      type="button"
                      className={`btn btn-primary alt`}
                      onClick={editSelectedTracks}
                    >
                      <i className="icon fas fa-pencil" aria-hidden="true"></i>
                      {t('common.button.editSelectedTracks')}
                    </button>
                  </div>
                  {/* Copy Product Metadata Button */}
                  <div className="track-reorder-buttons">
                    <button
                      type="button"
                      className={`btn btn-primary alt`}
                      onClick={handleCopyProductMetadataClick}
                    >
                      <i className="icon fas fa-clone" aria-hidden="true"></i>
                      {t('common.button.copyProductMetadata')}
                    </button>
                  </div>
                  {/* Add Side/Disc Button */}
                  {!isChangeRequestMode ? (
                    <div className="track-reorder-buttons">
                      <button
                        type="button"
                        className={`btn btn-primary alt`}
                        onClick={handleAddToVolumeClick}
                      >
                        <i className="icon fas fa-plus" aria-hidden="true"></i>
                        {usesSides
                          ? t('common.button.addToSide')
                          : t('common.button.addToDisc')}
                      </button>
                    </div>
                  ) : null}
                  {/* Delete Selected Tracks Button */}
                  {!isChangeRequestMode ? (
                    <div className="track-reorder-buttons">
                      <button
                        type="button"
                        className={`btn btn-primary alt`}
                        onClick={deleteSelectedTracks}
                      >
                        <i className="icon fas fa-times" aria-hidden="true"></i>
                        {t('common.button.deleteSelectedTracks')}
                      </button>
                    </div>
                  ) : null}
                </>
              ) : null}
              {/* Reorder Tracks Button */}
              {!isSelectedAllChecked &&
              !isSelectedAllIntermediate &&
              !isChangeRequestMode ? (
                <div className="track-reorder-buttons">
                  <button
                    type="button"
                    className={`btn btn-primary alt`}
                    onClick={() => setShowReorderTracks(true)}
                  >
                    <i className="icon fas fa-sort" aria-hidden="true"></i>
                    {t('common.button.reorderTracks')}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
          {volumeNumbers.map((item: number) => {
            let result = tracks
              .filter((x) => x.discOrSideNumber === item)
              .sort((x, y) => x.trackNumber - y.trackNumber);
            result =
              result.filter((x) => x.trackNumber === 0).length > 0
                ? []
                : result;
            return (
              <div className="track-container" key={item}>
                <div className="track-header">
                  {formatHeader(item)}
                  {!readonly ? (
                    <>
                      <VolumeMenu
                        showRemovebutton={volumeNumbers.length > 1}
                        onAddClickHandler={AddVolume}
                        volumeNumner={item}
                        usesSides={usesSides}
                        productViewMode={productViewMode}
                      ></VolumeMenu>
                      {!isChangeRequestMode ? (
                        <button
                          className="btn btn-primary alt btn-new-track"
                          onClick={() => addNewTrack(item)}
                        >
                          <i className="icon fas fa-plus"></i>
                          <span>{t('product.editProduct.newTrack')}</span>
                        </button>
                      ) : null}
                      {!isChangeRequestMode ? (
                        <button
                          className="btn btn-primary alt btn-multiple-tracks"
                          onClick={() => addMultipleTracks(item)}
                        >
                          <i className="icon fas fa-plus"></i>
                          <span>
                            {t('product.productMultipleTracks.multipleTracks')}
                          </span>
                        </button>
                      ) : null}
                    </>
                  ) : null}
                </div>
                <div className="track-tab-textalign-left">
                  <div className="ag-theme-alpine">
                    <AgGridReact
                      ref={(element) => {
                        if (element) {
                          gridsRef.current.set(String(item), element);
                        } else {
                          gridsRef.current.delete(String(item));
                        }
                      }}
                      rowSelection={!readonly ? rowSelection : 'single'}
                      onSelectionChanged={onSelectionChanged}
                      gridOptions={{ getLocaleText }}
                      rowData={result}
                      columnDefs={finalColumnDefs}
                      suppressNoRowsOverlay={true}
                      defaultColDef={{
                        suppressHeaderMenuButton: true,
                      }}
                      context={{
                        addRemoveTrack,
                        readonly,
                        result,
                        productViewMode,
                        trackChangeRequestChanges,
                      }}
                      onCellDoubleClicked={(e) => {
                        if (e.column.getColId() !== 'action' && e.data) {
                          toggleTrackPopUp(e.data);
                          e.event?.preventDefault();
                          e.event?.stopPropagation();
                        }
                      }}
                      theme="legacy"
                      columnMenu="legacy"
                      getRowId={getRowId}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}
    </>
  );
}
